import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../ui/Card/Card';
import Button from '../../ui/Button/Button';
import IconButton from '../../ui/IconButton';
import Icon from '../../ui/Icon';
import { ICONS } from '../../ui/icon-map';
import { TableContainer, Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../ui/Table/Table';
import { CellId, CellText, CellPlate, CellNumber, CellMoney, CellMuted, CellStatus } from '../../ui/DataTable/cells';
import Tabs from '../../ui/Tabs/Tabs';
import Stat, { StatGrid } from '../../ui/Stat/Stat';
import EmptyState from '../../ui/EmptyState';
import Dialog from '../../ui/Dialog/Dialog';
import { Field, Input } from '../../ui/Field/Field';
import { toast, confirmDialog } from '../../ui/feedback';
import LoadingSpinner from '../../components/LoadingSpinner';
import InventoryModal from './InventoryModal';
import ServiceModal from './ServiceModal';
import { fleetService, maintenanceService } from '../../services/firestore';
import { fleetServiceHistoryService } from '../../services/fleetServiceHistoryService';
import { usePermissions } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { normalizePlate as sharedNormalizePlate } from '../../services/possessionMatcher';
import { ensureSaved } from '../../services/writeResult';
import {
    nextServiceFor, newServiceRecord, newServiceRequest, savedServiceMessage, recordCostOf, partKeyOf, partKeyText,
    partGroupsOf, partGroupText, invoicesLineOf, invoicesCsvOf, dubaiDayOf,
} from '../../services/serviceRecords.logic';
import { inFleet, isGone } from '../../services/fleetStatus';
import { makeOilPlan } from '../../services/oilStatus';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import './FleetServiceHistory.css';
import './FleetServiceHistoryPrint.css'; // Print styles
import logger from '../../utils/logger';

// A record's cost: its kept total, or - missing / 0 - its lines at their OWN prices; null (shown "—") when a line
// has no price of its own. It used to reprice such a record at the batch's CURRENT price, so an invoice price edit
// rewrote old services (owner, 28 Sep 2026: one rule on the page, the CSV and the engine - recordCostOf).
const csvText = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;

export default function FleetServiceHistory() {
    const location = useLocation();
    const { hasPermission } = usePermissions();
    const [pickedTab, setActiveTab] = useState('service'); // service | inventory | upcoming | analytics
    // each button its own permission (permissions phase 3, 4 Oct 2026)
    const canAdjustLimits = hasPermission('service_adjust_limits');
    const canExport = hasPermission('export_service_history');
    const [statusMap, setStatusMap] = useState({});
    const [vehicles, setVehicles] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedVehicleFilter, setSelectedVehicleFilter] = useState('all');
    const [showVehicleDropdown, setShowVehicleDropdown] = useState(false);
    const [vehicleFilterSearch, setVehicleFilterSearch] = useState('');

    // Inventory State
    const [inventory, setInventory] = useState([]);
    const [inventorySearch, setInventorySearch] = useState(''); // Added missing state
    const [reportStartDate, setReportStartDate] = useState('');
    const [reportEndDate, setReportEndDate] = useState('');
    const [showInventoryModal, setShowInventoryModal] = useState(false);
    const [editingInventory, setEditingInventory] = useState(null);
    const [inventoryView, setInventoryView] = useState('consolidated'); // 'consolidated' | 'invoices'

    // Service State
    const [_selectedVehicle, _setSelectedVehicle] = useState(null);
    const [serviceRecords, setServiceRecords] = useState([]);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [printingVehicle, setPrintingVehicle] = useState(null); // State for the vehicle being printed
    const [serviceStartDate, setServiceStartDate] = useState('');
    const [serviceEndDate, setServiceEndDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [dataLoading, setDataLoading] = useState(true);

    const calculateRecordTotalCost = (record) => recordCostOf(record);
    // the sums (insights, totals, per model, per month) add the costs that are known; an unknown one is counted apart
    const knownCost = (record) => recordCostOf(record) ?? 0;
    const inventoryById = React.useMemo(() => new Map(inventory.map(i => [String(i.id), i])), [inventory]);

    // Calculate item consumption mapping (Total quantity used per itemId)
    const itemUsageMap = React.useMemo(() => {
        const usage = {};
        serviceRecords.forEach(record => {
            (record.items || []).forEach(item => {
                const id = item.itemId;
                if (!usage[id]) usage[id] = 0;
                usage[id] += Number(item.quantity) || 0;
            });
        });
        return usage;
    }, [serviceRecords]);


    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [adjustingVehicle, setAdjustingVehicle] = useState(null);
    const [adjustForm, setAdjustForm] = useState({ nextServiceKm: '', nextServiceDate: '' });
    
    // Professional Search & Filter for Upcoming Tab
    const [upcomingSearchTerm, setUpcomingSearchTerm] = useState('');
    const [upcomingStatusFilter, setUpcomingStatusFilter] = useState('all'); // all | DUE | SOON

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tab = queryParams.get('tab');
        if (tab === 'upcoming') setActiveTab('upcoming');

        const loadData = async () => {
            try {
                // Initial load of other non-realtime data
                const [cars, serviceData, statusRes] = await Promise.all([
                    fleetService.getAllCars(),
                    fleetServiceHistoryService.getServiceRecords(),
                    maintenanceService.getStatusMap()
                ]);
                // sold / stolen / Ready To Sell cars take no more services (was: only the exact, lower-case
                // "sold"); the Upcoming list narrows this to the cars in the fleet (DUP-03, FLT-14)
                const activeCars = cars.filter(c => !isGone(c.status));
                setVehicles(activeCars);
                setServiceRecords(serviceData);
                setStatusMap(statusRes.map || {});

                if (activeCars.length > 0) {
                    _setSelectedVehicle(activeCars[0]);
                }
            } catch (error) {
                logger.error('Error loading data:', error);
            } finally {
                setDataLoading(false);
            }
        };
        loadData();

        // One-time fetch for Inventory (no real-time listener needed)
        fleetServiceHistoryService.getInventory().then((data) => {
            const sortedData = [...data].sort((a, b) => {
                const aIsOil = a.name?.toLowerCase().includes('engine oil 5w-30');
                const bIsOil = b.name?.toLowerCase().includes('engine oil 5w-30');
                if (aIsOil && !bIsOil) return -1;
                if (!aIsOil && bIsOil) return 1;
                return 0;
            });
            setInventory(sortedData);
        });

        return () => {};
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // Calculate consolidated inventory for balance view
    const consolidatedInventory = React.useMemo(() => {
        const consolidated = {};
        inventory.forEach(item => {
            // one part = name + part number as a pair - the same key the service form and the engine's FIFO use
            // (a "name_pn" join made "a_b" + "" and "a" + "b_" one part)
            const key = partKeyText(partKeyOf(item.name, item.partNumber));

            if (!consolidated[key]) {
                consolidated[key] = {
                    id: key,
                    name: (item.name || 'Unnamed Item').trim(),
                    partNumber: (item.partNumber || '').trim(),
                    unit: item.unit,
                    quantity: 0,
                    minStock: 0,
                    totalValue: 0,
                    totalWeight: 0,
                    rawPriceSum: 0,
                    batchCount: 0,
                    ids: []
                };
            }
            const q = Number(item.quantity) || 0;
            const p = Number(item.unitCost) || 0;
            consolidated[key].quantity += q;
            consolidated[key].totalValue += q * p;
            consolidated[key].totalWeight += q;
            consolidated[key].rawPriceSum += p;
            consolidated[key].batchCount += 1;
            consolidated[key].ids.push(item.id);
            // Consolidate latest minStock or max seen
            consolidated[key].minStock = Math.max(consolidated[key].minStock, item.minStock || 0);
        });

        return Object.values(consolidated)
            .map(c => ({
                ...c,
                // Weighted Average Cost if there's stock, otherwise simple average of batch prices
                unitCost: c.totalWeight > 0 ? c.totalValue / c.totalWeight : (c.batchCount > 0 ? c.rawPriceSum / c.batchCount : 0)
            }))
            .sort((a, b) => {
                // Keep Engine Oil 5W-30 at the top
                const aIsOil = (a.name || '').toLowerCase().includes('engine oil 5w-30');
                const bIsOil = (b.name || '').toLowerCase().includes('engine oil 5w-30');
                if (aIsOil && !bIsOil) return -1;
                if (!aIsOil && bIsOil) return 1;
                // Otherwise alphabetical
                return (a.name || '').localeCompare(b.name || '');
            });
    }, [inventory]);





    const refreshInventory = async () => {
        const data = await fleetServiceHistoryService.getInventory();
        const sortedData = [...data].sort((a, b) => {
            const aIsOil = a.name?.toLowerCase().includes('engine oil 5w-30');
            const bIsOil = b.name?.toLowerCase().includes('engine oil 5w-30');
            if (aIsOil && !bIsOil) return -1;
            if (!aIsOil && bIsOil) return 1;
            return 0;
        });
        setInventory(sortedData);
    };

    // Inventory Functions
    const handleAddInventory = async (item, stayOpen = false) => {
        setIsSaving(true);
        try {
            if (editingInventory) {
                // the store: saved on the version the form opened - changed since (a service took parts from it),
                // nothing is saved and the list is read again
                const result = await fleetServiceHistoryService.updateInventoryItem(editingInventory.id, item, { version: editingInventory.version });
                if (!result.success) {
                    if (result.code === 'record-changed') await refreshInventory();
                    throw new Error(result.error);
                }
                setEditingInventory(null);
            } else {
                const result = await fleetServiceHistoryService.addInventoryItem(item);
                if (!result.success) throw new Error(result.error);
            }

            // no live listener: the list is read again after every save (a new item used to show only after a reload)
            await refreshInventory();
            if (!stayOpen) setShowInventoryModal(false);
            return { success: true };
        } catch (error) {
            logger.error('Error saving inventory:', error);
            toast.error('Failed to save inventory item: ' + error.message);
            return { success: false };
        } finally {
            setIsSaving(false);
        }
    };

    // Every car due or soon, before the search / status filter: the cars in the fleet (the one rule,
    // services/fleetStatus) with the one oil rule (services/oilStatus) - the same cars and the same DUE
    // as the Fleet page's "Oil Due" card that opens this tab (audit FLT-14)
    const allUpcomingServices = () => {
        const fleetVehicles = vehicles.filter((v) => inFleet(v));
        const kmByPlate = new Map(fleetVehicles.map((v) => [normalizePlate(v.plate_number), v.current_km]));
        const oilPlan = makeOilPlan({
            cars: fleetVehicles,
            serviceRecords,
            kmOf: (plate) => statusMap[normalizePlate(plate)]?.km || kmByPlate.get(normalizePlate(plate)),
        });
        return fleetVehicles.map((vehicle) => {
            const { label, currentKm, targetKm, targetDate, kmRemaining, daysRemaining } = oilPlan(vehicle.plate_number);
            return {
                ...vehicle,
                currentKm,
                targetKm,
                targetDate: targetDate ? targetDate.toLocaleDateString('en-GB') : 'No Date',
                kmRemaining,
                daysRemaining,
                status: label
            };
        }).filter(s => s.status === 'DUE' || s.status === 'SOON');
    };

    const getUpcomingServices = (all = allUpcomingServices()) => {
        const searchLower = upcomingSearchTerm.toLowerCase();
        let filtered = all;

        // Apply Status Filter
        if (upcomingStatusFilter !== 'all') {
            filtered = filtered.filter(s => s.status === upcomingStatusFilter);
        }

        // Apply Search Filter
        if (searchLower) {
            filtered = filtered.filter(s => 
                s.plate_number.toLowerCase().includes(searchLower) ||
                `${s.make} ${s.model}`.toLowerCase().includes(searchLower)
            );
        }

        return filtered.sort((a, b) => {
              if (a.status === 'DUE' && b.status !== 'DUE') return -1;
              if (a.status !== 'DUE' && b.status === 'DUE') return 1;
              return a.kmRemaining - b.kmRemaining;
          });
    };

    const handleUpdateLimits = async () => {
        if (!adjustingVehicle) return;
        setIsSaving(true);
        try {
            const updates = {
                nextServiceKmOverride: Number(adjustForm.nextServiceKm) || null,
                nextServiceDateOverride: adjustForm.nextServiceDate || null
            };
            // the limits are shown as saved only when the write was taken (audit FLT-04); the store: saved on the
            // version the car was opened with, and the row takes the new one
            const res = ensureSaved(await fleetService.updateCar(adjustingVehicle.plate_number, updates, { version: adjustingVehicle.version }), 'Service limits');
            const version = res?.car?.version;

            setVehicles(vehicles.map(v =>
                normalizePlate(v.plate_number) === normalizePlate(adjustingVehicle.plate_number) ? { ...v, ...updates, ...(version !== undefined ? { version } : {}) } : v
            ));
            
            setShowAdjustModal(false);
            setAdjustingVehicle(null);
        } catch (error) {
            logger.error('Error updating limits:', error);
            toast.error(`Failed to update limits: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };

    // Page-level wrapper: this page keys statusMap/lookups by lower-case-no-spaces plates.
    const normalizePlate = (plate) => sharedNormalizePlate(plate).toLowerCase();

    const handleAddService = async (service) => {
        if (!service.vehicleId) {
            toast.error('Please select a vehicle');
            return;
        }

        setIsSaving(true);
        try {

        if (editingService) {
            // Recalculate maintenance limits for the updated record. The store: the service's fields and its PARTS
            // (none when they did not change) - the engine gives changed parts back to their own invoices and takes
            // them again oldest first (owner, 28 Sep 2026: FIFO); it writes the lines and the total itself.
            const updatedRecord = USE_CONTRACTS_V2 ? newServiceRequest(service) : {
                ...service,
                ...nextServiceFor(service)
            };

            // The record and its stock move in one transaction: the parts saved on the record go
            // back, the new ones are taken - all written or none (audit FLT-19)
            // (the store: saved on the version the record was opened with; the records are read again after it)
            const result = await fleetServiceHistoryService.saveServiceRecordWithStock(editingService.id, updatedRecord, { version: editingService.version });
            await refreshInventory();
            if (result.success) {
                // the store: what the engine SAVED is said (it may differ from the preview when stock moved since)
                if (USE_CONTRACTS_V2) {
                    setServiceRecords(await fleetServiceHistoryService.getServiceRecords());
                    toast.success(savedServiceMessage(result.record, service.previewTotal));
                }
                else setServiceRecords(serviceRecords.map(r => r.id === editingService.id ? {
                    ...updatedRecord,
                    id: editingService.id
                } : r));
                setEditingService(null);
            } else {
                // the store refused a stale form (the record saved by someone else since, or the invoices its old
                // lines came from changed): the latest is read and the form closes, so it opens again on the latest -
                // left open, every retry sent the same old version / old parts and was refused again (review 28 Sep 2026)
                const stale = USE_CONTRACTS_V2 && (result.code === 'record-changed' || result.code === 'stock-changed');
                if (stale) setServiceRecords(await fleetServiceHistoryService.getServiceRecords());
                toast.error('Failed to update service: ' + result.error);
                if (stale) {
                    setShowServiceModal(false);
                    setEditingService(null);
                }
                return;
            }
        } else {
            // Generate Reference Number: YELO-yy/mm/0001 (atomic counter, race-safe). The store issues it itself when
            // it saves the record (one step with the stock), so the page asks for none there.
            const referenceNumber = USE_CONTRACTS_V2 ? '' : await fleetServiceHistoryService.nextReferenceNumber();

            // Next service from the SERVICE date (as an edit does) and the cost kept as it was
            // on the day (audit FLT-11). The store: the PARTS only - the engine takes them from the invoices oldest
            // first and writes the lines, the total and the number (owner, 28 Sep 2026: FIFO)
            const newRecord = USE_CONTRACTS_V2 ? newServiceRequest(service) : newServiceRecord(service, referenceNumber);

            // The record and the parts taken from stock in one transaction (audit FLT-19)
            const result = await fleetServiceHistoryService.saveServiceRecordWithStock(null, newRecord);
            await refreshInventory();
            if (result.success) {
                // the store: read again - the record comes back with the reference number the engine gave it, and
                // what it SAVED is said (it may differ from the preview when stock moved since)
                if (USE_CONTRACTS_V2) {
                    setServiceRecords(await fleetServiceHistoryService.getServiceRecords());
                    toast.success(savedServiceMessage(result.record, service.previewTotal));
                }
                else setServiceRecords([...serviceRecords, { ...newRecord, id: result.id }]);
                const serviced = vehicles.find(v => normalizePlate(v.plate_number) === normalizePlate(service.vehicleId));

                // CRITICAL: Clear overrides when a real service is recorded
                try {
                    const cleared = ensureSaved(await fleetService.updateCar(service.vehicleId, {
                        nextServiceKmOverride: null,
                        nextServiceDateOverride: null
                    }, { version: serviced?.version }), 'Clearing the service overrides');
                    const version = cleared?.car?.version;
                    setVehicles(vehicles.map(v =>
                        normalizePlate(v.plate_number) === normalizePlate(service.vehicleId)
                        ? { ...v, nextServiceKmOverride: null, nextServiceDateOverride: null, ...(version !== undefined ? { version } : {}) }
                        : v
                    ));
                } catch (e) {
                    logger.error("Non-blocking error clearing overrides:", e);
                    toast.warning(`Service saved, but the old next-service override is still set: ${e.message}`);
                }
            } else {
                toast.error('Failed to save service: ' + result.error);
                return;
            }
        }
        setShowServiceModal(false);
        } catch (error) {
            logger.error('Error saving service:', error);
            toast.error('Failed to save service record');
        } finally {
            setIsSaving(false);
        }
    };

    const exportToExcel = () => {
        const filteredRecords = serviceRecords.filter(record => {
            // Filter by vehicle
            if (selectedVehicleFilter !== 'all' && record.vehicleId !== selectedVehicleFilter) {
                return false;
            }
            // Filter by date range
            if (serviceStartDate && record.date < serviceStartDate) return false;
            if (serviceEndDate && record.date > serviceEndDate) return false;

            return true;
        });

        if (filteredRecords.length === 0) {
            toast.info('No records to export');
            return;
        }

        // Group by vehicle
        const groupedByVehicle = {};
        filteredRecords.forEach(record => {
            if (!groupedByVehicle[record.vehicleId]) {
                groupedByVehicle[record.vehicleId] = [];
            }
            groupedByVehicle[record.vehicleId].push(record);
        });

        // Create CSV content
        let csvContent = 'Ref No.,Plate Number,Make/Model,Service Date,Kilometers,Items Used,Invoices Used,Cost,Next Service (KM),Next Service (Date),Notes\n';

        Object.keys(groupedByVehicle).forEach(plateNumber => {
            const records = groupedByVehicle[plateNumber];
            const vehicle = vehicles.find(v => v.plate_number === plateNumber);
            const makeModel = vehicle ? `${vehicle.make} ${vehicle.model}` : '';

            csvContent += `\n=== ${plateNumber} - ${makeModel} ===\n`;
            csvContent += `Total Services: ${records.length}\n\n`;

            records.forEach(record => {
                // one entry per part (its lines summed), named from the line first, then its batch; the invoices
                // each part went out with, at their own prices (owner, 28 Sep 2026: FIFO)
                const groups = partGroupsOf(record.items, inventoryById);
                const itemsUsed = groups.map(partGroupText).join('; ');
                const invoicesUsed = groups.map(invoicesCsvOf).join('; ');
                const cost = calculateRecordTotalCost(record);

                csvContent += `${record.referenceNumber || '-'},${plateNumber},${makeModel},${record.date},${record.currentKm},${csvText(itemsUsed)},${csvText(invoicesUsed)},${cost ?? ''},${record.nextServiceKm},${record.nextServiceDate},"${record.notes || ''}"\n`;
            });
        });

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `service_history_${selectedVehicleFilter === 'all' ? 'all' : selectedVehicleFilter}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const exportInventoryToExcel = () => {
        // --- Section 1: Summary (All Time) ---
        let csvContent = '=== INVENTORY SUMMARY (ALL TIME) ===\n';
        csvContent += 'Item Name,Part Number,Unit,Unit Cost,Vendor,Total Bought,Total Used,Remaining,Min Stock\n';

        consolidatedInventory.forEach(item => {
            // Calculate total used for this part across all service records
            let totalUsedAllTime = 0;
            serviceRecords.forEach(record => {
                (record.items || []).forEach(usageItem => {
                    if (item.ids.includes(usageItem.itemId)) {
                        totalUsedAllTime += (Number(usageItem.quantity) || 0);
                    }
                });
            });

            // Calculate Total Bought = Remaining + Used
            const remaining = Number(item.quantity) || 0;
            const totalBought = remaining + totalUsedAllTime;

            csvContent += `"${item.name}","${item.partNumber || ''}",${item.unit},${item.unitCost || 0},"${item.vendor || ''}",${totalBought},${totalUsedAllTime},${remaining},${item.minStock || 0}\n`;
        });

        // --- Section 2: Usage Details (Filtered) ---
        csvContent += '\n\n=== USAGE DETAILS (TRANSACTION LOG) ===\n';
        csvContent += `Period: ${reportStartDate || 'Start'} to ${reportEndDate || 'Now'}\n`;
        csvContent += 'Date,Vehicle Plate,Item Name,Part Number,Quantity Used,Unit,Notes\n';

        // Filter by date range for the details section
        let filteredServices = serviceRecords;
        if (reportStartDate && reportEndDate) {
            filteredServices = serviceRecords.filter(record =>
                record.date >= reportStartDate && record.date <= reportEndDate
            );
        }

        if (filteredServices.length > 0) {
            filteredServices.forEach(record => {
                if (record.items && record.items.length > 0) {
                    record.items.forEach(usageItem => {
                        const invItem = inventory.find(i => i.id === usageItem.itemId);
                        if (invItem) {
                            csvContent += `${record.date},${record.vehicleId},"${invItem.name}","${invItem.partNumber || ''}",${usageItem.quantity},${invItem.unit},"${record.notes || ''}"\n`;
                        }
                    });
                }
            });
        } else {
            csvContent += 'No usage records found for this period.\n';
        }

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `inventory_master_report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = (plateNumber, singleRecord = null) => {
        const vehicle = vehicles.find(v => v.plate_number === plateNumber);

        // If singleRecord is provided, print only that. Otherwise print all records for the vehicle.
        const records = singleRecord
            ? [singleRecord]
            : serviceRecords.filter(r => r.vehicleId === plateNumber);

        if (!vehicle) return;

        setPrintingVehicle({
            details: vehicle,
            history: records
        });

        // State update triggers render, PrintableHistory will call window.print() when image loads
        const docTitle = singleRecord
            ? `Service Receipt - ${plateNumber}`
            : `Service History - ${plateNumber}`;
        document.title = docTitle;
        // logic moved to onImageLoaded prop
    };

    const handleFixRefDuplicates = async () => {
        if (!(await confirmDialog({
            title: 'Repair reference numbers?',
            message: 'This will search for and fix duplicate Reference Numbers. Proceed?',
            confirmLabel: 'Repair',
        }))) return;
        setIsSaving(true);
        try {
            const result = await fleetServiceHistoryService.fixDuplicateReferenceNumbers();
            if (result.success) {
                toast.success(`Successfully fixed ${result.fixedCount} duplicate reference numbers.`);
            } else {
                toast.error('Failed to fix duplicates: ' + result.error);
            }
        } catch (error) {
            logger.error('Error fixing duplicates:', error);
            toast.error('An unexpected error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteServiceRecord = async (record) => {
        if (deletingId) return; // Prevent double clicks
        if (await confirmDialog({
            title: 'Delete service record?',
            message: 'Delete this service record?',
            confirmLabel: 'Delete',
            danger: true,
        })) {
            setDeletingId(record.id);
            try {
                // The record goes and its parts come back to stock in one transaction: a failed
                // delete returns nothing, and a retry cannot return the parts twice (audit FLT-19)
                const result = await fleetServiceHistoryService.deleteServiceRecordWithStock(record.id);
                await refreshInventory();
                if (result.success) {
                    if (USE_CONTRACTS_V2) setServiceRecords(await fleetServiceHistoryService.getServiceRecords());
                    else setServiceRecords(serviceRecords.filter(r => r.id !== record.id));
                } else {
                    toast.error('Failed to delete: ' + result.error);
                }
            } catch (error) {
                logger.error("Error deleting service:", error);
                toast.error("An error occurred while deleting.");
            } finally {
                setDeletingId(null);
            }
        }
    };

    const handleDeleteInventoryItem = async (item) => {
        if (await confirmDialog({
            title: 'Delete inventory item?',
            message: 'Delete this inventory item?',
            confirmLabel: 'Delete',
            danger: true,
        })) {
            // the store: not deleted when it changed since the list was read (a service took parts from it)
            const result = await fleetServiceHistoryService.deleteInventoryItem(item.id, { version: item.version });
            if (!result.success) toast.error('Failed to delete: ' + result.error);
            // no live listener: the list is read again (a deleted item used to stay on screen until a reload)
            await refreshInventory();
        }
    };

    const everyUpcoming = allUpcomingServices();
    const upcomingServices = getUpcomingServices(everyUpcoming);
    // the tab badge counts every DUE car, whatever the search / status filter shows (= the Oil Due card)
    const upcomingDueCount = everyUpcoming.filter(s => s.status === 'DUE').length;

    const filteredServiceRecords = serviceRecords
        .filter(record => {
            // Filter by vehicle
            if (selectedVehicleFilter !== 'all' && record.vehicleId !== selectedVehicleFilter) {
                return false;
            }
            // Filter by date range
            if (serviceStartDate && record.date < serviceStartDate) return false;
            if (serviceEndDate && record.date > serviceEndDate) return false;
            // Filter by search
            if (!searchTerm) return true;
            const vehicle = vehicles.find(v => v.plate_number === record.vehicleId);
            const searchLower = searchTerm.toLowerCase();
            return (
                record.vehicleId?.toLowerCase().includes(searchLower) ||
                vehicle?.make?.toLowerCase().includes(searchLower) ||
                vehicle?.model?.toLowerCase().includes(searchLower)
            );
        })
        .sort((a, b) => (b.referenceNumber || '').localeCompare(a.referenceNumber || ''));

    const filteredConsolidatedInventory = consolidatedInventory.filter(item => {
        if (!inventorySearch) return true;
        const searchLower = inventorySearch.toLowerCase();
        return (
            (item.name && item.name.toLowerCase().includes(searchLower)) ||
            (item.partNumber && item.partNumber.toLowerCase().includes(searchLower))
        );
    });

    const filteredInvoiceInventory = inventory.filter(item => {
        let matchesSearch = true;
        let matchesDate = true;

        if (inventorySearch) {
            const searchLower = inventorySearch.toLowerCase();
            matchesSearch = (
                (item.name && item.name.toLowerCase().includes(searchLower)) ||
                (item.partNumber && item.partNumber.toLowerCase().includes(searchLower)) ||
                (item.invoiceNumber && String(item.invoiceNumber).toLowerCase().includes(searchLower))
            );
        }

        if (reportStartDate) matchesDate = matchesDate && (item.invoiceDate >= reportStartDate);
        if (reportEndDate) matchesDate = matchesDate && (item.invoiceDate <= reportEndDate);

        return matchesSearch && matchesDate;
    });

    const tabItems = [
        hasPermission(PERMISSIONS.view_service_tab) && { id: 'service', label: 'Service Records', icon: <Icon glyph={ICONS.maintenance} size="sm" /> },
        hasPermission(PERMISSIONS.view_inventory_tab) && { id: 'inventory', label: 'Inventory Management', icon: <Icon glyph={ICONS.operations} size="sm" /> },
        hasPermission('view_service_analytics_tab') && { id: 'analytics', label: 'Reports & Analytics', icon: <Icon glyph={ICONS.activity} size="sm" /> },
        hasPermission(PERMISSIONS.view_upcoming_tab) && {
            id: 'upcoming',
            label: 'Upcoming / Due',
            icon: <Icon glyph={ICONS.clock} size="sm" />,
            count: upcomingDueCount > 0 ? upcomingDueCount : undefined,
        },
    ].filter(Boolean);
    // only a tab the user holds shows (the default one and the ?tab=upcoming link included): else the first they hold
    const activeTab = tabItems.some((t) => t.id === pickedTab) ? pickedTab : tabItems[0]?.id;

    const unitAbbr = (unit) => (unit === 'Liter' ? 'L' : (unit === 'Piece' ? 'Pcs' : (unit ? `${unit}` : '')));

    const insightStyles = {
        warning: { box: 'bg-amber-50 border-amber-200', title: 'text-amber-900', icon: ICONS.warning },
        info: { box: 'bg-blue-50 border-blue-200', title: 'text-blue-900', icon: ICONS.bulb },
        note: { box: 'bg-gray-50 border-gray-200', title: 'text-gray-900', icon: ICONS.archive },
    };

    return (
        <Layout title="Fleet Service History">
            {/* Printable Component - Always in DOM but hidden via CSS until print */}
            <PrintableHistory
                data={printingVehicle}
                inventory={inventory}
                onImageLoaded={() => {
                   // Ensure browser has painted
                   setTimeout(() => {
                        window.print();
                        // Optional: Reset title here if needed, but usually fine to leave or reset on next print
                        // But we set title in handlePrint, so we should arguably restore it after print dialog closes
                        // However, JS execution pauses during print dialog in many browsers.
                   }, 50);
                }}
            />

            <div className="fleet-service-history">
                {/* Tabs */}
                <Tabs
                    items={tabItems}
                    value={activeTab}
                    onChange={setActiveTab}
                    label="Service history sections"
                    idPrefix="fsh"
                />

                {/* Upcoming Tab */}
                {activeTab === 'upcoming' && (
                    <Card
                        padding="none"
                        role="tabpanel"
                        id="fsh-panel-upcoming"
                        aria-labelledby="fsh-upcoming"
                        title="Upcoming Maintenance"
                        subtitle="Vehicles due or approaching service limit (10,000 KM or 6 Months)"
                        actions={(
                            <div className="ds-toolbar">
                                <div className="ds-search">
                                    <Icon glyph={ICONS.search} size="sm" />
                                    <input
                                        type="search"
                                        aria-label="Search plate or model"
                                        placeholder="Search plate or model..."
                                        value={upcomingSearchTerm}
                                        onChange={(e) => setUpcomingSearchTerm(e.target.value)}
                                    />
                                </div>

                                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" role="group" aria-label="Status filter">
                                    {['all', 'DUE', 'SOON'].map(status => (
                                        <Button
                                            key={status}
                                            size="xs"
                                            variant={upcomingStatusFilter === status ? 'secondary' : 'ghost'}
                                            aria-pressed={upcomingStatusFilter === status}
                                            onClick={() => setUpcomingStatusFilter(status)}
                                        >
                                            {status === 'all' ? 'All' : status === 'DUE' ? 'Due' : 'Soon'}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}
                    >
                        {dataLoading ? (
                            <LoadingSpinner message="Loading vehicles…" />
                        ) : upcomingServices.length === 0 ? (
                            <EmptyState
                                icon={ICONS.check}
                                title="Nothing due"
                                description="No vehicles currently matching filters."
                            />
                        ) : (
                            <TableContainer maxHeight="70vh" className="overflow-auto border-0 rounded-none">
                                <Table sticky>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader className="w-12">#</TableHeader>
                                            <TableHeader>Plate No</TableHeader>
                                            <TableHeader>Vehicle</TableHeader>
                                            <TableHeader>Current KM</TableHeader>
                                            <TableHeader>Next Service (KM)</TableHeader>
                                            <TableHeader>Next Service (Date)</TableHeader>
                                            <TableHeader>Status</TableHeader>
                                            <TableHeader>Actions</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {upcomingServices.map((car, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>
                                                    <CellPlate>{car.plate_number}</CellPlate>
                                                    {(car.nextServiceKmOverride || car.nextServiceDateOverride) && (
                                                        <CellMuted tone="warning">Manual Override</CellMuted>
                                                    )}
                                                </TableCell>
                                                <TableCell><CellText>{`${car.make || ''} ${car.model || ''}`.trim()}</CellText></TableCell>
                                                <TableCell><CellNumber value={car.currentKm} /></TableCell>
                                                <TableCell>
                                                    <CellNumber value={car.targetKm} />
                                                    <CellMuted tone={car.kmRemaining <= 0 ? 'danger' : car.kmRemaining <= 500 ? 'warning' : undefined}>
                                                        ({car.kmRemaining > 0 ? `${car.kmRemaining.toLocaleString()} KM left` : 'OVERDUE'})
                                                    </CellMuted>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="ds-cell-date">{car.targetDate}</span>
                                                    <CellMuted tone={car.daysRemaining === null ? undefined : car.daysRemaining <= 0 ? 'danger' : car.daysRemaining <= 10 ? 'warning' : undefined}>
                                                        ({car.daysRemaining !== null ? (car.daysRemaining > 0 ? `${car.daysRemaining} days left` : 'OVERDUE') : 'No Date'})
                                                    </CellMuted>
                                                </TableCell>
                                                <TableCell>
                                                    <CellStatus
                                                        status={car.status}
                                                        tone={car.status === 'DUE' ? 'danger' : 'warning'}
                                                        label={car.status}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex justify-center gap-1">
                                                        {canAdjustLimits && <IconButton
                                                            icon={ICONS.edit}
                                                            label="Adjust Limits"
                                                            size="sm"
                                                            onClick={() => {
                                                                setAdjustingVehicle(car);
                                                                // Parse targetDate back to YYYY-MM-DD if possible
                                                                let dateValue = '';
                                                                if (car.targetDate && car.targetDate !== 'No Date') {
                                                                    const parts = car.targetDate.split('/');
                                                                    if (parts.length === 3) dateValue = `${parts[2]}-${parts[1]}-${parts[0]}`;
                                                                }
                                                                setAdjustForm({
                                                                    nextServiceKm: car.targetKm,
                                                                    nextServiceDate: dateValue
                                                                });
                                                                setShowAdjustModal(true);
                                                            }}
                                                        />}
                                                        {hasPermission(PERMISSIONS.add_service_record) && <Button
                                                            size="sm"
                                                            onClick={() => {
                                                                _setSelectedVehicle(car);
                                                                setEditingService(null);
                                                                setShowServiceModal(true);
                                                            }}
                                                        >
                                                            Record Service
                                                        </Button>}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                )}

                {/* Adjustment Modal */}
                <Dialog
                    open={Boolean(showAdjustModal && adjustingVehicle)}
                    onClose={() => setShowAdjustModal(false)}
                    title="Adjust Service Limits"
                    size="sm"
                    closeOnOverlay={false}
                    footer={(
                        <>
                            <Button onClick={() => setShowAdjustModal(false)} disabled={isSaving}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={handleUpdateLimits} isLoading={isSaving}>
                                Save Adjustments
                            </Button>
                        </>
                    )}
                >
                    {adjustingVehicle && (
                        <div className="flex flex-col gap-4">
                            <div>
                                <div className="text-xs font-medium text-gray-500">Vehicle</div>
                                <div className="text-base font-semibold font-mono text-gray-900">{adjustingVehicle.plate_number}</div>
                                <div className="text-sm text-gray-600">{adjustingVehicle.make} {adjustingVehicle.model}</div>
                            </div>

                            <Field label="New Next Service KM" help="This will override the automatic 10,000 KM calculation.">
                                <Input
                                    type="number"
                                    value={adjustForm.nextServiceKm}
                                    onChange={(e) => setAdjustForm(prev => ({ ...prev, nextServiceKm: e.target.value }))}
                                    placeholder="e.g. 10400"
                                />
                            </Field>

                            <Field label="New Next Service Date" help="This will override the automatic 6-month calculation.">
                                <Input
                                    type="date"
                                    value={adjustForm.nextServiceDate}
                                    onChange={(e) => setAdjustForm(prev => ({ ...prev, nextServiceDate: e.target.value }))}
                                />
                            </Field>
                        </div>
                    )}
                </Dialog>

                {activeTab === 'service' && (
                    <Card
                        padding="none"
                        role="tabpanel"
                        id="fsh-panel-service"
                        aria-labelledby="fsh-service"
                        title="Vehicle Service Records"
                        actions={(
                            <div className="ds-toolbar">
                                {canExport && <Button onClick={exportToExcel} size="sm" leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                                    Export
                                </Button>}
                                {canExport && selectedVehicleFilter !== 'all' && (
                                    <Button
                                        onClick={() => handlePrint(selectedVehicleFilter)}
                                        size="sm"
                                        leadingIcon={<Icon glyph={ICONS.print} size="sm" />}
                                    >
                                        Print Full History
                                    </Button>
                                )}
                                {hasPermission(PERMISSIONS.add_service_record) && (
                                    <Button onClick={() => setShowServiceModal(true)} variant="primary" size="sm" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                        Record Service
                                    </Button>
                                )}
                            </div>
                        )}
                    >
                        <div className="ds-toolbar px-4 py-3 border-b border-gray-200">
                            <div className="vehicle-search-container w-full sm:w-56">
                                <Input
                                    type="text"
                                    size="sm"
                                    aria-label="Filter by vehicle"
                                    autoComplete="off"
                                    value={selectedVehicleFilter === 'all' ? vehicleFilterSearch : vehicles.find(v => v.plate_number === selectedVehicleFilter)?.plate_number || ''}
                                    onChange={(e) => {
                                        setVehicleFilterSearch(e.target.value);
                                        setSelectedVehicleFilter('all');
                                        setShowVehicleDropdown(true);
                                    }}
                                    onFocus={() => setShowVehicleDropdown(true)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            const filtered = vehicles.filter(v =>
                                                v.plate_number.toLowerCase().includes(vehicleFilterSearch.toLowerCase()) ||
                                                `${v.make} ${v.model}`.toLowerCase().includes(vehicleFilterSearch.toLowerCase())
                                            );
                                            if (filtered.length > 0) {
                                                setSelectedVehicleFilter(filtered[0].plate_number);
                                                setShowVehicleDropdown(false);
                                            }
                                        }
                                    }}
                                    placeholder="All Vehicles - Search..."
                                />
                                {showVehicleDropdown && vehicleFilterSearch && (
                                    <div className="vehicle-dropdown">
                                        <button
                                            type="button"
                                            className="vehicle-option"
                                            onClick={() => {
                                                setSelectedVehicleFilter('all');
                                                setVehicleFilterSearch('');
                                                setShowVehicleDropdown(false);
                                            }}
                                        >
                                            <strong>All Vehicles</strong>
                                        </button>
                                        {vehicles
                                            .filter(v =>
                                                v.plate_number.toLowerCase().includes(vehicleFilterSearch.toLowerCase()) ||
                                                `${v.make} ${v.model}`.toLowerCase().includes(vehicleFilterSearch.toLowerCase())
                                            )
                                            .slice(0, 10)
                                            .map(v => (
                                                <button
                                                    type="button"
                                                    key={v.plate_number}
                                                    className="vehicle-option"
                                                    onClick={() => {
                                                        setSelectedVehicleFilter(v.plate_number);
                                                        setVehicleFilterSearch('');
                                                        setShowVehicleDropdown(false);
                                                    }}
                                                >
                                                    <strong>{v.plate_number}</strong> - {v.make} {v.model}
                                                </button>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                            <div className="ds-search">
                                <Icon glyph={ICONS.search} size="sm" />
                                <input
                                    type="search"
                                    aria-label="Search by plate number or model"
                                    placeholder="Search by plate number or model..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <label htmlFor="fsh-service-from">From</label>
                                <Input
                                    id="fsh-service-from"
                                    type="date"
                                    size="sm"
                                    value={serviceStartDate}
                                    onChange={(e) => setServiceStartDate(e.target.value)}
                                />
                                <label htmlFor="fsh-service-to">To</label>
                                <Input
                                    id="fsh-service-to"
                                    type="date"
                                    size="sm"
                                    value={serviceEndDate}
                                    onChange={(e) => setServiceEndDate(e.target.value)}
                                />
                            </div>
                        </div>

                        {dataLoading ? (
                            <LoadingSpinner message="Loading service records…" />
                        ) : filteredServiceRecords.length === 0 ? (
                            <EmptyState
                                icon={ICONS.history}
                                title="No service records"
                                description={serviceRecords.length === 0 ? 'Recorded services will appear here.' : 'No records match the current filters.'}
                            />
                        ) : (
                            <TableContainer maxHeight="70vh" className="overflow-auto border-0 rounded-none">
                                <Table sticky>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>#</TableHeader>
                                            <TableHeader>Ref No.</TableHeader>
                                            <TableHeader>Plate Number</TableHeader>
                                            <TableHeader>Make/Model</TableHeader>
                                            <TableHeader>Chassis Number</TableHeader>
                                            <TableHeader>Color</TableHeader>
                                            <TableHeader>Date</TableHeader>
                                            <TableHeader>Kilometers</TableHeader>
                                            <TableHeader>Items Used</TableHeader>
                                            <TableHeader>Next Service (KM)</TableHeader>
                                            <TableHeader>Next Service (Date)</TableHeader>
                                            <TableHeader>Total Cost</TableHeader>
                                            <TableHeader>Notes</TableHeader>
                                            <TableHeader>Actions</TableHeader>
                                            <TableHeader>Print</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredServiceRecords.map((record, idx) => {
                                            const vehicle = vehicles.find(v => v.plate_number === record.vehicleId);
                                            const [year, month, day] = record.date.split('-');
                                            const formattedDate = `${day}/${month}/${year}`;
                                            const [nYear, nMonth, nDay] = record.nextServiceDate.split('-');
                                            const formattedNextDate = `${nDay}/${nMonth}/${nYear}`;
                                            return (
                                                <TableRow key={record.id}>
                                                    <TableCell>{idx + 1}</TableCell>
                                                    <TableCell><CellId>{record.referenceNumber}</CellId></TableCell>
                                                    <TableCell><CellPlate>{record.vehicleId}</CellPlate></TableCell>
                                                    <TableCell><CellText>{`${vehicle?.make || ''} ${vehicle?.model || ''}`.trim()}</CellText></TableCell>
                                                    <TableCell><CellText>{vehicle?.vin}</CellText></TableCell>
                                                    <TableCell><CellText>{vehicle?.color}</CellText></TableCell>
                                                    <TableCell><span className="ds-cell-date">{formattedDate}</span></TableCell>
                                                    <TableCell className="whitespace-nowrap"><CellNumber value={record.currentKm} /> km</TableCell>
                                                    <TableCell>
                                                        <ul className="flex flex-col gap-1 min-w-[200px]">
                                                            {/* one entry per part (its lines summed), named from the line first, then its batch;
                                                                the invoices it went out with below (owner, 28 Sep 2026: FIFO) */}
                                                            {(() => {
                                                                const groups = partGroupsOf(record.items, inventoryById);
                                                                return groups.map((g, gIdx) => (
                                                                    <li key={gIdx} className={`text-sm pb-0.5 ${gIdx < groups.length - 1 ? 'border-b border-dotted border-gray-200' : ''}`}>
                                                                        {g.known ? (
                                                                            <span>
                                                                                {g.name}: <span>({g.quantity}{unitAbbr(g.unit)})</span>
                                                                                {g.partNumber && <span className="ml-1 font-mono text-xs text-gray-500">({g.partNumber})</span>}
                                                                            </span>
                                                                        ) : (
                                                                            <span className="text-red-700">{g.name} (Not Found)</span>
                                                                        )}
                                                                        {g.lines.some(l => l?.invoiceNumber) && (
                                                                            <span className="block text-xs text-gray-700">{invoicesLineOf(g)}</span>
                                                                        )}
                                                                    </li>
                                                                ));
                                                            })()}
                                                        </ul>
                                                    </TableCell>
                                                    <TableCell className="whitespace-nowrap"><CellNumber value={record.nextServiceKm} /> km</TableCell>
                                                    <TableCell><span className="ds-cell-date">{formattedNextDate}</span></TableCell>
                                                    <TableCell>
                                                        <CellMoney value={calculateRecordTotalCost(record)} currency="AED" />
                                                    </TableCell>
                                                    <TableCell><CellText>{record.notes}</CellText></TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-1">
                                                            {hasPermission(PERMISSIONS.edit_service_record) && (
                                                                <IconButton
                                                                    icon={ICONS.edit}
                                                                    label="Edit Service"
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setEditingService(record);
                                                                        setShowServiceModal(true);
                                                                    }}
                                                                />
                                                            )}
                                                            {hasPermission(PERMISSIONS.delete_service_record) && (
                                                                <IconButton
                                                                    icon={ICONS.delete}
                                                                    label="Delete Service"
                                                                    size="sm"
                                                                    tone="danger"
                                                                    isLoading={deletingId === record.id}
                                                                    onClick={() => handleDeleteServiceRecord(record)}
                                                                />
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {canExport && <IconButton
                                                            icon={ICONS.print}
                                                            label="Print This Record"
                                                            size="sm"
                                                            onClick={() => handlePrint(record.vehicleId, record)}
                                                        />}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                )}

                {/* Inventory Tab */}
                {activeTab === 'inventory' && (
                    <Card
                        padding="none"
                        role="tabpanel"
                        id="fsh-panel-inventory"
                        aria-labelledby="fsh-inventory"
                        title="Parts & Oil Inventory"
                        subtitle="Manage your stock and track purchase invoices"
                        actions={hasPermission(PERMISSIONS.add_inventory_item) ? (
                            <Button onClick={() => setShowInventoryModal(true)} variant="primary" size="sm" leadingIcon={<Icon glyph={ICONS.plus} size="sm" />}>
                                Add New Invoice/Batch
                            </Button>
                        ) : null}
                    >
                        <div className="ds-toolbar px-4 py-3 border-b border-gray-200">
                            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" role="group" aria-label="Inventory view">
                                <Button
                                    size="xs"
                                    variant={inventoryView === 'consolidated' ? 'secondary' : 'ghost'}
                                    aria-pressed={inventoryView === 'consolidated'}
                                    onClick={() => setInventoryView('consolidated')}
                                >
                                    Current Balance
                                </Button>
                                {hasPermission(PERMISSIONS.view_inventory_purchase_history) && (
                                    <Button
                                        size="xs"
                                        variant={inventoryView === 'invoices' ? 'secondary' : 'ghost'}
                                        aria-pressed={inventoryView === 'invoices'}
                                        onClick={() => setInventoryView('invoices')}
                                    >
                                        Purchase History (IN)
                                    </Button>
                                )}
                            </div>

                            <div className="ds-toolbar__spacer" />

                            <div className="ds-search">
                                <Icon glyph={ICONS.search} size="sm" />
                                <input
                                    type="search"
                                    aria-label="Search parts"
                                    placeholder="Search parts..."
                                    value={inventorySearch}
                                    onChange={(e) => setInventorySearch(e.target.value)}
                                />
                            </div>

                            {inventoryView === 'invoices' && (
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <label htmlFor="fsh-inv-from">Date</label>
                                    <Input
                                        id="fsh-inv-from"
                                        type="date"
                                        size="sm"
                                        value={reportStartDate}
                                        onChange={(e) => setReportStartDate(e.target.value)}
                                    />
                                    <label htmlFor="fsh-inv-to" className="sr-only">To</label>
                                    <span aria-hidden="true">–</span>
                                    <Input
                                        id="fsh-inv-to"
                                        type="date"
                                        size="sm"
                                        value={reportEndDate}
                                        onChange={(e) => setReportEndDate(e.target.value)}
                                    />
                                </div>
                            )}

                            {canExport && <Button size="sm" onClick={exportInventoryToExcel} leadingIcon={<Icon glyph={ICONS.export} size="sm" />}>
                                Report
                            </Button>}
                        </div>

                        {(inventoryView === 'consolidated' ? filteredConsolidatedInventory : filteredInvoiceInventory).length === 0 ? (
                            <EmptyState
                                icon={ICONS.database}
                                title="No inventory items"
                                description={inventory.length === 0 ? 'Add an invoice or batch to start tracking stock.' : 'No items match the current search or dates.'}
                            />
                        ) : (
                            <TableContainer maxHeight="70vh" className="overflow-auto border-0 rounded-none">
                                <Table sticky>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeader>#</TableHeader>
                                            <TableHeader>Item Name</TableHeader>
                                            <TableHeader>Part Number</TableHeader>
                                            <TableHeader>Unit</TableHeader>
                                            {inventoryView === 'invoices' && (
                                                <>
                                                    <TableHeader>Inward Qty</TableHeader>
                                                    <TableHeader>Consumed Qty</TableHeader>
                                                </>
                                            )}
                                            <TableHeader>Available Qty</TableHeader>
                                            <TableHeader>Min Stock</TableHeader>
                                            {inventoryView === 'invoices' && (
                                                <>
                                                    <TableHeader>Invoice No.</TableHeader>
                                                    <TableHeader>Invoice Date</TableHeader>
                                                </>
                                            )}
                                            <TableHeader>Unit Cost</TableHeader>
                                            {inventoryView === 'invoices' && <TableHeader>Vendor</TableHeader>}
                                            <TableHeader>Status</TableHeader>
                                            <TableHeader>Actions</TableHeader>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {inventoryView === 'consolidated' ? (
                                            // Consolidated Balance Table Rows
                                            filteredConsolidatedInventory.map((item, idx) => {
                                                const isLow = item.quantity <= item.minStock;
                                                return (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{idx + 1}</TableCell>
                                                        <TableCell><CellText>{item.name}</CellText></TableCell>
                                                        <TableCell><CellId>{item.partNumber}</CellId></TableCell>
                                                        <TableCell><CellText>{item.unit}</CellText></TableCell>
                                                        <TableCell>
                                                            <CellNumber value={item.quantity} tone={isLow ? 'danger' : undefined} />
                                                        </TableCell>
                                                        <TableCell><CellNumber value={item.minStock} /></TableCell>
                                                        <TableCell>
                                                            <CellMoney value={item.unitCost || 0} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <CellStatus status={isLow ? 'low' : 'ok'} tone={isLow ? 'warning' : 'success'} label={isLow ? 'Low' : 'OK'} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <CellMuted>Consolidated</CellMuted>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            // Batch/Invoice History Table Rows
                                            filteredInvoiceInventory.map((item, idx) => {
                                                const [year, month, day] = (item.invoiceDate || '').split('-');
                                                const formattedDate = item.invoiceDate ? `${day}/${month}/${year}` : '-';
                                                const isLow = item.quantity <= item.minStock;
                                                return (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{idx + 1}</TableCell>
                                                        <TableCell><CellText>{item.name}</CellText></TableCell>
                                                        <TableCell><CellId>{item.partNumber}</CellId></TableCell>
                                                        <TableCell><CellText>{item.unit}</CellText></TableCell>
                                                        <TableCell>
                                                            <CellNumber value={Number(item.quantity) + (itemUsageMap[item.id] || 0)} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <CellNumber value={itemUsageMap[item.id] || 0} tone={itemUsageMap[item.id] ? 'danger' : undefined} />
                                                        </TableCell>
                                                        <TableCell><CellNumber value={item.quantity} /></TableCell>
                                                        <TableCell><CellNumber value={item.minStock} /></TableCell>
                                                        <TableCell><CellId>{item.invoiceNumber}</CellId></TableCell>
                                                        <TableCell><span className="ds-cell-date">{formattedDate}</span></TableCell>
                                                        <TableCell><CellMoney value={item.unitCost || 0} /></TableCell>
                                                        <TableCell><CellText>{item.vendor}</CellText></TableCell>
                                                        <TableCell>
                                                            <CellStatus status={isLow ? 'low' : 'ok'} tone={isLow ? 'warning' : 'success'} label={isLow ? 'Low' : 'OK'} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex gap-1">
                                                                {hasPermission(PERMISSIONS.edit_inventory_item) && (
                                                                    <IconButton
                                                                        icon={ICONS.edit}
                                                                        label="Edit Item"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            setEditingInventory(item);
                                                                            setShowInventoryModal(true);
                                                                        }}
                                                                    />
                                                                )}
                                                                {hasPermission(PERMISSIONS.delete_inventory_item) && (
                                                                    <IconButton
                                                                        icon={ICONS.delete}
                                                                        label="Delete Item"
                                                                        size="sm"
                                                                        tone="danger"
                                                                        onClick={() => handleDeleteInventoryItem(item)}
                                                                    />
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Card>
                )}

                {/* Modals */}
                {showInventoryModal && (
                    <InventoryModal
                        onClose={() => {
                            setShowInventoryModal(false);
                            setEditingInventory(null);
                        }}
                        onSave={handleAddInventory}
                        editData={editingInventory}
                        isSaving={isSaving}
                        itemUsageMap={itemUsageMap}
                    />
                )}

                {showServiceModal && (
                    <ServiceModal
                        onClose={() => {
                            setShowServiceModal(false);
                            setEditingService(null);
                        }}
                        onSave={handleAddService}
                        vehicle={vehicles}
                        inventory={inventory}
                        editData={editingService}
                        serviceRecords={serviceRecords}
                        isSaving={isSaving}
                    />
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                    <div className="flex flex-col gap-6" role="tabpanel" id="fsh-panel-analytics" aria-labelledby="fsh-analytics">

                        {/* Smart Insights Section */}
                        <section>
                            <div className="flex flex-wrap gap-2 justify-between items-center mb-3">
                                <h3 className="ds-section-title" style={{ margin: 0 }}>
                                    <Icon glyph={ICONS.activity} size="sm" tone="muted" /> Smart Insights & Data Integrity
                                </h3>
                                {/* the store issues each reference number once, from one sequence: nothing to repair there */}
                                {!USE_CONTRACTS_V2 && hasPermission('service_repair_refs') && (
                                    <Button
                                        size="sm"
                                        onClick={handleFixRefDuplicates}
                                        leadingIcon={<Icon glyph={ICONS.settings} size="sm" />}
                                    >
                                        Repair Ref Numbers
                                    </Button>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {(() => {
                                    const insights = [];

                                    // 1. High Maintenance Cost Alert
                                    const vehicleCosts = {};
                                    serviceRecords.forEach(r => {
                                        if (!vehicleCosts[r.vehicleId]) vehicleCosts[r.vehicleId] = 0;
                                        vehicleCosts[r.vehicleId] += knownCost(r);
                                    });

                                    const avgCost = Object.values(vehicleCosts).length > 0
                                        ? Object.values(vehicleCosts).reduce((a, b) => a + b, 0) / Object.values(vehicleCosts).length
                                        : 0;

                                    Object.entries(vehicleCosts).forEach(([vId, cost]) => {
                                        if (cost > avgCost * 2 && avgCost > 0) {
                                            insights.push({
                                                type: 'warning',
                                                title: `High Expense: ${vId}`,
                                                text: `This vehicle's maintenance cost (${cost.toLocaleString()} AED) is 100% higher than the fleet average. Recommend technical inspection.`
                                            });
                                        }
                                    });

                                    // 2. Bulk Purchase Suggestion
                                    const itemUsageCount = {};
                                    const thirtyDaysAgo = new Date();
                                    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

                                    serviceRecords.filter(r => new Date(r.date) >= thirtyDaysAgo).forEach(r => {
                                        r.items?.forEach(i => {
                                            if (!itemUsageCount[i.itemId]) itemUsageCount[i.itemId] = 0;
                                            itemUsageCount[i.itemId] += Number(i.quantity);
                                        });
                                    });

                                    Object.entries(itemUsageCount).forEach(([itemId, qty]) => {
                                        if (qty >= 5) {
                                            const item = inventory.find(inv => inv.id === itemId);
                                            insights.push({
                                                type: 'info',
                                                title: `Bulk Buy: ${item?.name || 'Item'}`,
                                                text: `High consumption detected (${qty} units in 30 days). Consider bulk ordering to reduce unit costs.`
                                            });
                                        }
                                    });

                                    // 3. Slow Moving Stock
                                    inventory.forEach(item => {
                                        const lastUsed = serviceRecords
                                            .filter(r => r.items?.some(i => i.itemId === item.id))
                                            .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

                                        const ninetyDaysAgo = new Date();
                                        ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

                                        if (item.quantity > 0 && (!lastUsed || new Date(lastUsed.date) < ninetyDaysAgo)) {
                                            insights.push({
                                                type: 'note',
                                                title: `Slow Moving: ${item.name}`,
                                                text: `This item has not been used for over 90 days. Avoid overstocking this part in the future.`
                                            });
                                        }
                                    });

                                    // 4. Duplicate Part Number Detection
                                    const partMap = {};
                                    inventory.forEach(item => {
                                        if (item.partNumber) {
                                            const pn = item.partNumber.trim();
                                            const inv = (item.invoiceNumber || '').trim();
                                            const key = `${pn}-${inv}`;
                                            if (!partMap[key]) partMap[key] = [];
                                            partMap[key].push(item);
                                        }
                                    });

                                    Object.entries(partMap).forEach(([key, items]) => {
                                        if (items.length > 1) {
                                            const [pn, inv] = key.split('-');
                                            const totalInward = items.reduce((sum, item) => sum + (Number(item?.quantity || 0) + (itemUsageMap[item.id] || 0)), 0);

                                            if (inv) {
                                                insights.push({
                                                    type: 'warning',
                                                    title: `Duplicate Entry: ${pn}`,
                                                    text: `Potential error! ${items.length} records found with same PN and Invoice (${inv}). Total Inward combined: ${totalInward}.`
                                                });
                                            } else {
                                                insights.push({
                                                    type: 'info',
                                                    title: `Same PN (Multiple Batches): ${pn}`,
                                                    text: `This part exists in ${items.length} records. Total Inward across all: ${totalInward}.`
                                                });
                                            }
                                        }
                                    });

                                    if (insights.length === 0) {
                                        return (
                                            <Card className="md:col-span-2 xl:col-span-3">
                                                <EmptyState
                                                    icon={ICONS.check}
                                                    title="No suggestions"
                                                    description="No optimization suggestions at this time. Keep monitoring your data!"
                                                />
                                            </Card>
                                        );
                                    }

                                    return insights.slice(0, 3).map((insight, idx) => {
                                        const look = insightStyles[insight.type] || insightStyles.note;
                                        return (
                                            <div key={idx} className={`flex flex-col gap-2 rounded-lg border p-4 ${look.box}`}>
                                                <div className={`flex items-center gap-2 text-sm font-semibold ${look.title}`}>
                                                    <Icon glyph={look.icon} size="sm" /> {insight.title}
                                                </div>
                                                <div className="text-sm text-gray-700 leading-snug">{insight.text}</div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </section>

                        {/* Full Fleet Maintenance Projection */}
                        <Card
                            title="Full Fleet Maintenance Projection (1013 Cars)"
                        >
                            {(() => {
                                // 1. Model Mapping (Intelligence Layer) - Learning from history
                                const modelPartMapping = {};
                                serviceRecords.forEach(record => {
                                    const car = vehicles.find(v => v.plate_number === record.vehicleId);
                                    if (!car) return;
                                    const modelKey = `${car.make} ${car.model}`;
                                    if (!modelPartMapping[modelKey]) modelPartMapping[modelKey] = { filters: {}, oils: {}, oilQtySum: 0, oilUsageCount: 0 };

                                    record.items?.forEach(usage => {
                                        const item = inventory.find(i => i.id === usage.itemId);
                                        if (!item) return;
                                        const name = item.name.toLowerCase();
                                        if (name.includes('filter')) {
                                            modelPartMapping[modelKey].filters[item.id] = (modelPartMapping[modelKey].filters[item.id] || 0) + 1;
                                        } else if (name.includes('oil')) {
                                            modelPartMapping[modelKey].oils[item.id] = (modelPartMapping[modelKey].oils[item.id] || 0) + 1;
                                            modelPartMapping[modelKey].oilQtySum += (Number(usage.quantity) || 0);
                                            modelPartMapping[modelKey].oilUsageCount += 1;
                                        }
                                    });
                                });

                                const resolvedParts = {};
                                Object.entries(modelPartMapping).forEach(([model, maps]) => {
                                    resolvedParts[model] = {
                                        filterId: Object.entries(maps.filters).sort((a,b) => b[1] - a[1])[0]?.[0],
                                        oilId: Object.entries(maps.oils).sort((a,b) => b[1] - a[1])[0]?.[0],
                                        avgOilQty: maps.oilUsageCount > 0 ? maps.oilQtySum / maps.oilUsageCount : 4
                                    };
                                });

                                // 2. Fallbacks
                                const allFilters = inventory.filter(i => i.name?.toLowerCase().includes('filter'));
                                const avgFilter = allFilters.length > 0 ? allFilters.reduce((s, i) => s + (i.unitCost || 0), 0) / allFilters.length : 0;
                                const allOils = inventory.filter(i => i.name?.toLowerCase().includes('oil'));
                                const avgOil = allOils.length > 0 ? allOils.reduce((s, i) => s + (i.unitCost || 0), 0) / allOils.length : 0;

                                // 3. Calculation
                                const breakdown = {};
                                let totalCost = 0;
                                vehicles.forEach(v => {
                                    const key = `${v.make} ${v.model}`;
                                    if (!breakdown[key]) breakdown[key] = { count: 0, filterPrice: 0, oilPrice: 0, oilQty: 4, isLearned: false };
                                    breakdown[key].count++;

                                    const mapping = resolvedParts[key];
                                    const f = inventory.find(i => i.id === mapping?.filterId);
                                    const o = inventory.find(i => i.id === mapping?.oilId);

                                    breakdown[key].filterPrice = f ? f.unitCost : avgFilter;
                                    breakdown[key].oilPrice = o ? o.unitCost : avgOil;
                                    breakdown[key].oilQty = mapping ? mapping.avgOilQty : 4;
                                    breakdown[key].isLearned = !!(f || o);
                                });

                                Object.values(breakdown).forEach(d => {
                                    totalCost += d.count * (d.filterPrice + (d.oilPrice * d.oilQty));
                                });

                                return (
                                    <>
                                        <StatGrid min={200} className="mb-4">
                                            <Stat
                                                label="Active Fleet Size"
                                                icon={ICONS.fleet}
                                                value={`${vehicles.length} Cars`}
                                                meta="Dynamic from Fleet Manager"
                                            />
                                            <Stat
                                                label="Total Budget Projection"
                                                icon={ICONS.wallet}
                                                value={`${totalCost.toLocaleString('en-US', { maximumFractionDigits: 0 })} AED`}
                                                meta="Learned Oil Qty + Filter per Car"
                                            />
                                        </StatGrid>

                                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Intelligence Breakdown by Model</h4>
                                        {Object.keys(breakdown).length === 0 ? (
                                            <p className="text-sm text-gray-500">No vehicles loaded.</p>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 max-h-[240px] overflow-y-auto pr-1">
                                                {Object.entries(breakdown).sort((a,b) => b[1].count - a[1].count).map(([model, data]) => (
                                                    <Card key={model} padding="sm">
                                                    <div className="flex justify-between items-center gap-3 text-sm">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="font-medium text-gray-900">{model} <span className="font-normal text-gray-500">({data.count})</span></div>
                                                            <div className={`text-xs ${data.isLearned ? 'text-green-700' : 'text-gray-500'}`}>
                                                                {data.isLearned ? `Smart: ${data.oilQty.toFixed(1)}L Oil + Filter` : 'Using fleet averages'}
                                                            </div>
                                                        </div>
                                                        <div className="text-right font-mono font-medium whitespace-nowrap">
                                                            {(data.count * (data.filterPrice + (data.oilPrice * data.oilQty))).toLocaleString()} AED
                                                        </div>
                                                    </div>
                                                    </Card>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </Card>

                        {/* KPI Cards */}
                        <StatGrid min={220}>
                            <Stat
                                label="Total Maintenance Spending"
                                icon={ICONS.wallet}
                                value={`${serviceRecords.reduce((sum, r) => sum + knownCost(r), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })} AED`}
                                meta={(() => {
                                    // a record whose cost is unknown (a line with no price of its own) is not guessed
                                    const unknownCosts = serviceRecords.filter(r => calculateRecordTotalCost(r) === null).length;
                                    return unknownCosts > 0 ? `${unknownCosts} record(s) without a cost` : undefined;
                                })()}
                            />
                            <Stat
                                label="Total Service Jobs"
                                icon={ICONS.maintenance}
                                value={serviceRecords.length}
                            />
                            <Stat
                                label="Low Stock Items"
                                icon={ICONS.warning}
                                value={inventory.filter(i => i.quantity <= i.minStock).length}
                                tone={inventory.filter(i => i.quantity <= i.minStock).length > 0 ? 'warning' : undefined}
                            />
                        </StatGrid>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Top Vehicles by Spend */}
                            <Card title="Top Vehicles by Spending" padding="none">
                                {serviceRecords.length === 0 ? (
                                    <EmptyState icon={ICONS.chart} title="No service records yet" />
                                ) : (
                                    <TableContainer className="border-0 rounded-none">
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeader>Vehicle Model</TableHeader>
                                                    <TableHeader>Service Jobs</TableHeader>
                                                    <TableHeader>Aggregated Spent</TableHeader>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(() => {
                                                    const spendingMap = {};
                                                    serviceRecords.forEach(r => {
                                                        const v = vehicles.find(veh => veh.plate_number === r.vehicleId);
                                                        const modelKey = v ? `${v.make} ${v.model}` : r.vehicleId;

                                                        if (!spendingMap[modelKey]) spendingMap[modelKey] = { count: 0, total: 0, plates: new Set() };
                                                        spendingMap[modelKey].count++;
                                                        spendingMap[modelKey].total += knownCost(r);
                                                        spendingMap[modelKey].plates.add(r.vehicleId);
                                                    });
                                                    return Object.entries(spendingMap)
                                                        .sort((a, b) => b[1].total - a[1].total)
                                                        .slice(0, 5)
                                                        .map(([modelName, data]) => {
                                                            return (
                                                                <TableRow key={modelName}>
                                                                    <TableCell>
                                                                        <CellText>{modelName}</CellText>
                                                                        <CellMuted>
                                                                            {data.plates.size} Vehicle{data.plates.size > 1 ? 's' : ''}
                                                                        </CellMuted>
                                                                    </TableCell>
                                                                    <TableCell><CellNumber value={data.count} /></TableCell>
                                                                    <TableCell><CellMoney value={data.total} currency="AED" /></TableCell>
                                                                </TableRow>
                                                            );
                                                        });
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Card>

                            {/* Most Used Inventory Items */}
                            <Card title="Most Consumed Items" padding="none">
                                {serviceRecords.length === 0 ? (
                                    <EmptyState icon={ICONS.chart} title="No consumption yet" />
                                ) : (
                                    <TableContainer className="border-0 rounded-none">
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeader>Item</TableHeader>
                                                    <TableHeader>Total Used</TableHeader>
                                                    <TableHeader>Remaining</TableHeader>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(() => {
                                                    const consumedMap = {};
                                                    serviceRecords.forEach(r => {
                                                        r.items?.forEach(i => {
                                                            if (!consumedMap[i.itemId]) consumedMap[i.itemId] = 0;
                                                            consumedMap[i.itemId] += (Number(i.quantity) || 0);
                                                        });
                                                    });
                                                    return Object.entries(consumedMap)
                                                        .sort((a, b) => b[1] - a[1])
                                                        .slice(0, 5)
                                                        .map(([itemId, qty]) => {
                                                            const item = inventory.find(inv => inv.id === itemId);
                                                            return (
                                                                <TableRow key={itemId}>
                                                                    <TableCell>
                                                                        <CellText>{item?.name || 'Unknown'}</CellText>
                                                                        <CellMuted>{item?.partNumber}</CellMuted>
                                                                    </TableCell>
                                                                    <TableCell className="whitespace-nowrap"><CellNumber value={qty} /> {item?.unit}</TableCell>
                                                                    <TableCell><CellNumber value={item?.quantity} tone={item?.quantity <= item?.minStock ? 'danger' : undefined} /></TableCell>
                                                                </TableRow>
                                                            );
                                                        });
                                                })()}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Card>
                        </div>

                        {/* Monthly Spending Trend */}
                        <Card title="Monthly Spending Trend">
                            <div className="flex flex-col gap-3">
                                {(() => {
                                    const monthlyMap = {};
                                    // grouped by yyyy-mm of the service's Dubai day, newest first, English month names (review
                                    // 18 Oct 2026: it took 6 months in the records' storage order, in the browser's language)
                                    serviceRecords.forEach(r => {
                                        const day = dubaiDayOf(r.date);
                                        if (!day) return;
                                        const ym = day.slice(0, 7);
                                        monthlyMap[ym] = (monthlyMap[ym] || 0) + knownCost(r);
                                    });
                                    const monthName = (ym) => new Date(Date.UTC(+ym.slice(0, 4), +ym.slice(5, 7) - 1, 1))
                                        .toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

                                    const months = Object.entries(monthlyMap).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 6)
                                        .map(([ym, total]) => [monthName(ym), total]); // Last 6 months
                                    const maxSpending = Math.max(...months.map(m => m[1]), 1);

                                    if (months.length === 0) {
                                        return <p className="text-sm text-gray-500">No spending recorded yet.</p>;
                                    }

                                    return months.map(([month, total]) => (
                                        <div key={month} className="flex flex-wrap sm:flex-nowrap items-center gap-3 text-sm">
                                            <div className="w-32 shrink-0 text-gray-600">{month}</div>
                                            <div className="fsh-bar" role="presentation">
                                                <div className="fsh-bar__fill" style={{ width: `${(total / maxSpending) * 100}%` }} />
                                            </div>
                                            <div className="w-28 shrink-0 text-right font-mono font-medium">
                                                {total.toLocaleString()} AED
                                            </div>
                                        </div>
                                    ));
                                })()}
                            </div>
                        </Card>
                    </div>
                )}
            </div>
        </Layout>
    );
}

// Helper Component for Printable Area (rendered outside main layout flow but inside component)
function PrintableHistory({ data, inventory, onImageLoaded }) {
    if (!data) return null;
    const { details, history } = data;

    return (
        <div id="printable-area">
            {/* Background Letterhead Image - using fixed position to stay as background */}
            <img
                src="/Mock.jpg"
                alt=""
                onLoad={() => {
                    if (data && onImageLoaded) onImageLoaded();
                }}
                onError={() => {
                     // If image fails, print anyway so user isn't stuck
                     if (data && onImageLoaded) onImageLoaded();
                }}
                style={{
                    position: 'fixed', /* Fixed allows it to stay relative to voltage/page, acting as background */
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'fill', /* Changed to fill to ensuring it stretches exactly to page bounds as a letterhead */
                    zIndex: -1
                }}
            />

            {/* Content Container - Ensure it has transparent background */}
            <div style={{ position: 'relative', zIndex: 1, paddingTop: '3cm', paddingLeft: '15px', paddingRight: '15px' }}>

                {/* Vehicle Details */}
                <div style={{ marginBottom: '10px' }}>

                    {/* Reference Number Display on Receipt */}
                    {data.history.length === 1 && data.history[0].referenceNumber && (
                        <div style={{
                            position: 'absolute',
                            top: '3.5cm',
                            right: '2cm',
                            fontSize: '12px',
                            fontWeight: 'bold'
                        }}>
                            Ref: {data.history[0].referenceNumber}
                        </div>
                    )}

                    <table style={{ width: '100%', marginBottom: '10px', fontSize: '12px', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr>
                                <td style={{ padding: '5px', fontWeight: 'bold', width: '15%' }}>Plate Number:</td>
                                <td style={{ padding: '5px', width: '35%' }}>{details.plate_number}</td>
                                <td style={{ padding: '5px', fontWeight: 'bold', width: '15%' }}>Make/Model:</td>
                                <td style={{ padding: '5px', width: '35%' }}>{details.make} {details.model}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '5px', fontWeight: 'bold' }}>Chassis Number:</td>
                                <td style={{ padding: '5px' }}>{details.vin || '-'}</td>
                                <td style={{ padding: '5px', fontWeight: 'bold' }}>Color:</td>
                                <td style={{ padding: '5px' }}>{details.color || '-'}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Separator */}
                <hr style={{ border: '0', borderTop: '1px solid #ccc', marginBottom: '20px' }} />

                {/* Title */}
                <h3 style={{ textAlign: 'center', marginBottom: '15px', textDecoration: 'underline' }}>Vehicle Service History</h3>

                {/* History Table */}
                <table className="print-table">
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Kilometers</th>
                            <th>Items Used</th>
                            <th>Next Service (KM)</th>
                            <th>Next Service (Date)</th>
                            <th>Notes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.length === 0 ? (
                            <tr><td colSpan="6" style={{ textAlign: 'center' }}>No service history found.</td></tr>
                        ) : (
                            history.sort((a, b) => new Date(b.date) - new Date(a.date)).map((record, idx) => {
                                const [year, month, day] = record.date.split('-');
                                const formattedDate = `${day}/${month}/${year}`;
                                const [nYear, nMonth, nDay] = record.nextServiceDate.split('-');
                                const formattedNextDate = `${nDay}/${nMonth}/${nYear}`;

                                // one entry per part and its total quantity (a part taken from two invoices is one part)
                                const itemsDesc = partGroupsOf(record.items, inventory).map(partGroupText).join(', ');

                                return (
                                    <tr key={idx}>
                                        <td>{formattedDate}</td>
                                        <td>{record.currentKm} km</td>
                                        <td>{itemsDesc}</td>
                                        <td>{record.nextServiceKm} km</td>
                                        <td>{formattedNextDate}</td>
                                        <td>{record.notes}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
